// Email validation  
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email.trim())
}

// Password validation - at least 8 chars, 1 uppercase, 1 lowercase, 1 number
export const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = []
  
  if (password.length < 8) {
    errors.push('Passwort muss mindestens 8 Zeichen lang sein')
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Passwort muss mindestens einen Großbuchstaben enthalten')
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Passwort muss mindestens einen Kleinbuchstaben enthalten')
  }
  
  if (!/\d/.test(password)) {
    errors.push('Passwort muss mindestens eine Zahl enthalten')
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

// Enhanced HTML sanitization to prevent XSS attacks
export const sanitizeInput = (input: string): string => {
  return input.trim()
    // Remove HTML tags completely
    .replace(/<[^>]*>/g, '')
    // Remove javascript: protocol
    .replace(/javascript:/gi, '')
    // Remove data: protocol (can be used for XSS)
    .replace(/data:/gi, '')
    // Remove event handlers
    .replace(/on\w+\s*=/gi, '')
    // Remove script content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove style content that could contain expressions
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    // Remove potential CSS expressions
    .replace(/expression\s*\(/gi, '')
    // Remove potential unicode escapes that could be XSS
    .replace(/\\u[0-9a-fA-F]{4}/g, '')
    // Remove HTML entities that could be used for obfuscation
    .replace(/&[#x]?[0-9a-fA-F]+;/gi, '')
    // Limit length to prevent DoS
    .substring(0, 10000)
}

// Validate phone number (German format)
export const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^(\+49|0)[1-9]\d{1,14}$/
  return phoneRegex.test(phone.replace(/\s+/g, ''))
}

// Validate name (no special characters except hyphens and apostrophes)
export const validateName = (name: string): boolean => {
  const nameRegex = /^[a-zA-ZäöüÄÖÜß\-'\s]{1,50}$/
  return nameRegex.test(name.trim())
}

// Rate limiting helper (client-side)
class RateLimiter {
  private attempts: Map<string, number[]> = new Map()
  
  isAllowed(key: string, maxAttempts: number = 5, windowMs: number = 300000): boolean {
    const now = Date.now()
    const windowStart = now - windowMs
    
    // Get existing attempts for this key
    const existingAttempts = this.attempts.get(key) || []
    
    // Filter out attempts outside the window
    const validAttempts = existingAttempts.filter(time => time > windowStart)
    
    // Check if we're within the limit
    if (validAttempts.length >= maxAttempts) {
      return false
    }
    
    // Add this attempt
    validAttempts.push(now)
    this.attempts.set(key, validAttempts)
    
    return true
  }
  
  getTimeToReset(key: string, windowMs: number = 300000): number {
    const attempts = this.attempts.get(key) || []
    if (attempts.length === 0) return 0
    
    const oldestAttempt = Math.min(...attempts)
    const resetTime = oldestAttempt + windowMs
    
    return Math.max(0, resetTime - Date.now())
  }
}

export const rateLimiter = new RateLimiter()